'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/expandable-menu-button/styles.css';
import { EXPANDABLE_MENU_BUTTON_TEST_ID, isLongMenuButtonText } from 'BomdisoftComponents/expandable-menu-button/render';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { Tooltip, type TooltipPlacement } from 'BomdisoftComponents/tooltip/index';
import { BadgeColor } from 'BomdisoftComponents/types';
import { dummy } from '@bomdisoft/utilities';
import { type ComponentProps, ReactNode, type Ref } from 'react';

/** Every native button attribute the caller may pass through to the button
 * itself. `className` is left out on purpose: this component draws two boxes
 * and `containerClassName` / `buttonClassName` say which one is meant.
 *
 * `onClick` comes from here rather than being redeclared over `HTMLElement`.
 * This renders a `<button>`, so the button's own handler type is the true one,
 * and the wider hand-written version refused a handler typed for the element
 * it actually receives. */
type PassThroughProps = Omit<ComponentProps<'button'>, 'className' | 'ref' | 'type'>;

export type ExpandableMenuButtonProps = PassThroughProps & {
  annotation?: string;
  annotationAction?: string;
  /** Declared, because `ComponentProps<'button'>` has no index signature for
   * `data-*`: JSX accepts them on an element, a props TYPE does not, so the
   * resolution below cannot read one that is not named here. */
  'data-testid'?: string;
  text?: string;
  icon: ReactNode;
  badgeContent?: ReactNode;
  badgeColor?: BadgeColor;
  tooltip?: string;
  /** Shows the text beside the icon. Without it the button is the icon alone
   * and the label rides the tooltip: the collapsed sidebar rail, and since
   * 2026-09-20 the grid toolbar on a phone, which drives it from a media
   * query rather than from a rail's open state. */
  expanded?: boolean;
  isActive?: boolean;
  showBadge?: boolean;
  showTooltip?: boolean;
  placement?: TooltipPlacement;
  containerClassName?: string;
  buttonClassName?: string;
  textClassName?: string;
  /** `null` stamps no test id at all, for a host whose buttons are addressed
   * some other way. Omitting it keeps the shared default, which several admin
   * consoles' journeys already drive. */
  testId?: string | null;
  ariaLabel?: string;
  /** The button element, for a caller that anchors an overlay to it. */
  ref?: Ref<HTMLButtonElement>;
};

export const ExpandableMenuButton = ({
  annotation,
  annotationAction,
  text,
  tooltip,
  icon,
  expanded,
  showBadge,
  placement = 'top',
  onClick = dummy,
  containerClassName,
  buttonClassName,
  textClassName,
  badgeContent = '',
  badgeColor = 'default',
  isActive,
  showTooltip = true,
  testId = EXPANDABLE_MENU_BUTTON_TEST_ID,
  ariaLabel,
  ref,
  ...rest
}: ExpandableMenuButtonProps) => {
  const isLongText = isLongMenuButtonText(text);

  /* An explicit `data-testid` beats the shared default, so a host with its own
     naming does not have to opt out of one it never asked for. */
  const { 'data-testid': passedTestId, ...attributes } = rest;
  const stampedTestId = passedTestId ?? testId ?? undefined;

  const iconComponent = showBadge ? (
    <span className='bomdisoft-expandable-menu-button-badge-holder'>
      {icon}
      <span className='bomdisoft-expandable-menu-button-badge' data-color={badgeColor}>
        {badgeContent}
      </span>
    </span>
  ) : (
    icon
  );

  const menuButton = (
    <button {...attributes} aria-label={ariaLabel || tooltip || text} className={joinClasses('bomdisoft-expandable-menu-button', containerClassName)} data-prodantix={annotation} data-prodantix-action={annotationAction} data-testid={stampedTestId} onClick={onClick} ref={ref} type='button'>
      <span className={joinClasses('bomdisoft-expandable-menu-button-button', buttonClassName)} data-active={isActive ? '' : undefined} data-expanded={expanded ? '' : undefined}>
        {iconComponent}
        {text ? <span className={joinClasses('bomdisoft-expandable-menu-button-text', textClassName)}>{text}</span> : null}
      </span>
    </button>
  );

  return !isLongText && (expanded || !showTooltip) ? (
    menuButton
  ) : (
    <Tooltip placement={placement} title={tooltip || text}>
      {menuButton}
    </Tooltip>
  );
};
